/**
 * useBenchmarkRuns / useBenchmarkRunDetail Hooks
 *
 * Real-time Firestore listeners for benchmark run data.
 *
 * useBenchmarkRuns: Listens to top-level `benchmark_runs` collection,
 *   ordered by submitted_at desc (most recent first).
 *
 * useBenchmarkRunDetail: Listens to both the run doc AND its `scenarios`
 *   sub-collection for per-scenario results.
 */

import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
    getFirestore,
    collection,
    query,
    orderBy,
    limit,
    onSnapshot,
    doc,
} from 'firebase/firestore';

const FIRESTORE_DATABASE_ID = 'vortx-flux-site-dna';

function getDb() {
    return getFirestore(getApp(), FIRESTORE_DATABASE_ID);
}

function normalizeTimestamp(value) {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (value.toDate && typeof value.toDate === 'function') {
        return value.toDate().toISOString();
    }
    if (value.seconds) {
        return new Date(value.seconds * 1000).toISOString();
    }
    return null;
}

/**
 * useBenchmarkRuns - Real-time list of benchmark runs.
 *
 * Listens to: benchmark_runs (top-level collection)
 * Ordered by submitted_at desc.
 *
 * @param {Object} params
 * @param {number} [params.maxResults=20]
 * @returns {{ runs: Array, loading: boolean, error: Error|null }}
 */
export function useBenchmarkRuns({ maxResults = 20 } = {}) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);

        let unsubscribe;
        try {
            const db = getDb();
            const runsCollectionRef = collection(db, 'benchmark_runs');

            const runsQuery = query(
                runsCollectionRef,
                orderBy('submitted_at', 'desc'),
                limit(maxResults)
            );

            unsubscribe = onSnapshot(
                runsQuery,
                (snapshot) => {
                    const results = snapshot.docs.map((d) => {
                        const data = d.data();
                        return {
                            run_id: d.id,
                            status: data.status,
                            suite: data.suite,
                            label: data.label,
                            submitted_at: normalizeTimestamp(data.submitted_at),
                            completed_at: normalizeTimestamp(data.completed_at),
                            progress: data.progress || {},
                            total_scenarios: data.total_scenarios || 0,
                            summary: data.summary || null,
                        };
                    });
                    setRuns(results);
                    setLoading(false);
                    setError(null);
                },
                (err) => {
                    console.error('[useBenchmarkRuns] Firestore listener error:', err);
                    setError(err);
                    setLoading(false);
                }
            );
        } catch (err) {
            console.error('[useBenchmarkRuns] Failed to set up listener:', err);
            setError(err);
            setLoading(false);
        }

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [maxResults]);

    return { runs, loading, error };
}

/**
 * useBenchmarkRunDetail - Real-time detail for a single benchmark run.
 *
 * Listens to: benchmark_runs/{runId} (run doc)
 *             benchmark_runs/{runId}/scenarios (sub-collection)
 *
 * @param {string} runId
 * @returns {{ run: Object|null, scenarios: Array, loading: boolean }}
 */
export function useBenchmarkRunDetail(runId) {
    const [run, setRun] = useState(null);
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!runId) {
            setRun(null);
            setScenarios([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        const db = getDb();
        const unsubscribers = [];

        try {
            // Listen to run document
            const runDocRef = doc(db, 'benchmark_runs', runId);
            const unsubRun = onSnapshot(
                runDocRef,
                (snapshot) => {
                    if (!snapshot.exists()) {
                        setRun(null);
                    } else {
                        const data = snapshot.data();
                        setRun({
                            run_id: snapshot.id,
                            status: data.status,
                            suite: data.suite,
                            label: data.label,
                            submitted_at: normalizeTimestamp(data.submitted_at),
                            completed_at: normalizeTimestamp(data.completed_at),
                            progress: data.progress || { completed: 0, total: 0 },
                            total_scenarios: data.total_scenarios || 0,
                            summary: data.summary || null,
                            training_report: data.training_report || null,
                        });
                    }
                    setLoading(false);
                },
                (err) => {
                    console.error('[useBenchmarkRunDetail] Run listener error:', err);
                    setLoading(false);
                }
            );
            unsubscribers.push(unsubRun);

            // Listen to scenarios sub-collection
            const scenariosRef = collection(db, 'benchmark_runs', runId, 'scenarios');
            const scenariosQuery = query(scenariosRef, orderBy('scenario_id', 'asc'));
            const unsubScenarios = onSnapshot(
                scenariosQuery,
                (snapshot) => {
                    const results = snapshot.docs.map((d) => {
                        const data = d.data();
                        return {
                            scenario_id: d.id,
                            ...data,
                            started_at: normalizeTimestamp(data.started_at),
                            completed_at: normalizeTimestamp(data.completed_at),
                        };
                    });
                    setScenarios(results);
                },
                (err) => {
                    console.error('[useBenchmarkRunDetail] Scenarios listener error:', err);
                }
            );
            unsubscribers.push(unsubScenarios);
        } catch (err) {
            console.error('[useBenchmarkRunDetail] Failed to set up listeners:', err);
            setLoading(false);
        }

        return () => {
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [runId]);

    return { run, scenarios, loading };
}
